import { useEffect, useMemo, useState } from 'react';
import {
  Provider,
  ProviderResultStatus,
  formatSoum,
  isPendingStatus,
  type HistoryResponse,
  type HistoryRow,
  type StoreOption,
} from '@asko/shared';
import { AppHeader } from '../components/AppHeader';
import { DateField } from '../components/DateField';
import { pageNumbers } from '../lib/pagination';
import { EventTimeline } from '../components/EventTimeline';
import { Select, type SelectOption } from '../components/Select';
import { ApiError, api } from '../lib/api';
import { formatDateTime, formatTime } from '../lib/format';
import { useI18n } from '../lib/i18n';

/**
 * Do'konlar tarixi (ma'muriyat).
 *
 * Savol, uni yozib beradigan: qaysi mijoz, qachon, qaysi do'konda va
 * sotuvchida qanday limit oldi. Shuning uchun bitta qator = bitta
 * scoring so'rovi, ichida ikkala provayder javobi yonma-yon.
 *
 * Filtrlar manzilga yozilmaydi: ekran ish quroli, havola bilan
 * bo'lishish talabi yo'q. Sahifa esa filtr o'zgarganda birinchisiga
 * qaytadi — aks holda «hech narsa topilmadi» bo'lib ko'rinadi.
 */

/** Sahifada nechta qator ko'rsatish mumkin. */
const PAGE_SIZES = [10, 20, 50, 100] as const;

/** Yozishni tugatgunicha kutamiz — har harfga so'rov ketmasin. */
const SEARCH_DELAY_MS = 400;
/** Kalendarda ortiqcha yillar chiqmasin: tizim shundan oldin yo'q edi. */
const EARLIEST = '2026-01-01';
const ALL = '' as const;

interface Filters {
  storeId: string;
  salesUserId: string;
  from: string;
  to: string;
  q: string;
}

const EMPTY: Filters = {
  storeId: ALL,
  salesUserId: ALL,
  from: '',
  to: '',
  q: '',
};

export function HistoryPage() {
  const { t, lang, apiMessage } = useI18n();

  const [filters, setFilters] = useState<Filters>(EMPTY);
  // Qidiruv maydoni har bosishda so'rov yubormaydi — tugma yoki Enter.
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[1]!);
  /** Qaysi qator ochilgan — hodisalar tasmasi bilan. */
  const [opened, setOpened] = useState<string | null>(null);
  /** Qaysi mijozning hamma urinishlari ko'rsatilyapti. */
  const [clientFor, setClientFor] = useState<HistoryRow['client'] | null>(null);

  const [data, setData] = useState<HistoryResponse | null>(null);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [sellers, setSellers] = useState<StoreOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    api.get<StoreOption[]>('/admin/stores').then(setStores).catch(() => setStores([]));
  }, []);

  // Sotuvchilar ro'yxati tanlangan do'konga bog'liq.
  useEffect(() => {
    const path = filters.storeId ? `/admin/sales-users?storeId=${filters.storeId}` : '/admin/sales-users';
    api.get<StoreOption[]>(path).then(setSellers).catch(() => setSellers([]));
  }, [filters.storeId]);

  useEffect(() => {
    let stopped = false;
    setLoading(true);

    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }

    api
      .get<HistoryResponse>(`/admin/history?${params.toString()}`)
      .then((result) => {
        if (!stopped) {
          setData(result);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!stopped) setError(err instanceof ApiError ? err : null);
      })
      .finally(() => {
        if (!stopped) setLoading(false);
      });

    return () => {
      stopped = true;
    };
  }, [filters, page, pageSize]);

  /**
   * Qidiruv yozilgan sari ishlaydi, lekin har bosishda emas.
   *
   * Tanaffussiz qilinsa, «Madatov» so'zi yettita so'rov yuboradi va
   * javoblar tartibsiz qaytishi mumkin — ekranda oxirgi emas, eng
   * sekin javob qolib ketardi.
   */
  useEffect(() => {
    const next = search.trim();
    // Bitta harf bo'yicha qidirishning ma'nosi yo'q (server ham 2 tadan
    // kam qabul qilmaydi), lekin tozalash darrov ishlashi kerak.
    const value = next.length >= 2 ? next : '';
    if (value === filters.q) return;

    const timer = setTimeout(() => change({ q: value }), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [search]);

  function change(patch: Partial<Filters>) {
    setFilters((before) => ({ ...before, ...patch }));
    setPage(1);
  }

  const storeOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: ALL, label: t('history.allStores') },
      ...stores.map((store) => ({ value: store.id, label: store.name })),
    ],
    [stores, t],
  );

  const sellerOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: ALL, label: t('history.allSellers') },
      ...sellers.map((seller) => ({ value: seller.id, label: seller.name })),
    ],
    [sellers, t],
  );

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const dirty = JSON.stringify(filters) !== JSON.stringify(EMPTY);

  return (
    <div className="app-shell">
      <AppHeader />

      <main className="app-main app-main--wide">
        <div className="card">
          <div className="history-head">
            <div>
              <h1 className="section-title">{t('history.title')}</h1>
              <p className="section-hint">{t('history.hint')}</p>
            </div>
            <span className="history-total">{t('history.total', { n: total })}</span>
          </div>

          {/*
            Har bir filtr — yorliq va boshqaruv. Yorliqsiz qoldirilsa,
            kalendar (u yorliqni o'zi chizadi) qatordan chiqib ketadi va
            ro'yxatlar bilan bir chiziqda turmaydi.
          */}
          <div className="history-filters">
            <label className="history-filters__field">
              <span className="field__label">{t('history.store')}</span>
              <Select
                value={filters.storeId}
                options={storeOptions}
                onChange={(storeId) => change({ storeId, salesUserId: ALL })}
                ariaLabel={t('history.store')}
              />
            </label>

            <label className="history-filters__field">
              <span className="field__label">{t('history.seller')}</span>
              <Select
                value={filters.salesUserId}
                options={sellerOptions}
                onChange={(salesUserId) => change({ salesUserId })}
                ariaLabel={t('history.seller')}
              />
            </label>

            <DateField
              className="history-filters__field"
              label={t('history.from')}
              value={filters.from}
              onChange={(from) => change({ from })}
              min={EARLIEST}
            />
            <DateField
              className="history-filters__field"
              label={t('history.to')}
              value={filters.to}
              onChange={(to) => change({ to })}
              min={EARLIEST}
            />

            <label className="history-filters__field history-filters__field--wide">
              <span className="field__label">{t('history.search')}</span>
              <input
                className="field__input"
                placeholder={t('history.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t('history.search')}
              />
            </label>
          </div>

          <div className="history-filters__actions">
            {dirty && (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => {
                  setSearch('');
                  setFilters(EMPTY);
                  setPage(1);
                }}
              >
                {t('history.reset')}
              </button>
            )}
          </div>

          {error && (
            <div className="alert alert--error" role="alert">
              {apiMessage(error)}
            </div>
          )}

          {loading && !data ? (
            <div className="center-screen" aria-busy="true">
              <div className="spinner" aria-label={t('common.loading')} />
            </div>
          ) : total === 0 ? (
            <p className="section-hint">{t('history.empty')}</p>
          ) : (
            <div className="history-scroll">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>{t('history.date')}</th>
                    <th>{t('history.client')}</th>
                    <th>{t('history.store')}</th>
                    <th>{t('history.seller')}</th>
                    <th>Alif Nasiya</th>
                    <th>Uzum Nasiya</th>
                    <th>{t('history.chosen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.rows.map((row) => (
                    <HistoryTableRow
                      key={row.sessionId}
                      row={row}
                      lang={lang}
                      opened={opened === row.sessionId}
                      onToggle={() =>
                        setOpened(opened === row.sessionId ? null : row.sessionId)
                      }
                      onOpenClient={() => setClientFor(row.client)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="history-pager">
            <label className="history-pager__size">
              <span className="field__label">{t('history.perPage')}</span>
              <Select
                value={String(pageSize)}
                options={PAGE_SIZES.map((size) => ({
                  value: String(size),
                  label: String(size),
                }))}
                onChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
                ariaLabel={t('history.perPage')}
              />
            </label>

            {pages > 1 && (
              <nav className="history-pager__pages" aria-label={t('history.pages')}>
                <button
                  className="btn btn--small"
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  {t('history.prev')}
                </button>

                {/* Sahifalar ko'p bo'lsa hammasi chiqarilmaydi: chetlari
                    va joriysining atrofi qoladi, orasi «…» bilan. */}
                {pageNumbers(page, pages).map((item, index) =>
                  item === null ? (
                    <span key={`gap-${index}`} className="history-pager__gap">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      className={`btn btn--small ${item === page ? 'is-active' : ''}`}
                      type="button"
                      aria-current={item === page ? 'page' : undefined}
                      onClick={() => setPage(item)}
                      disabled={loading}
                    >
                      {item}
                    </button>
                  ),
                )}

                <button
                  className="btn btn--small"
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages || loading}
                >
                  {t('history.next')}
                </button>
              </nav>
            )}
          </div>
        </div>
      </main>

      {clientFor && (
        <dialog
          className="modal modal--wide"
          ref={(node) => {
            if (node && !node.open) node.showModal();
          }}
          onCancel={(e) => {
            e.preventDefault();
            setClientFor(null);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setClientFor(null);
          }}
        >
          <div className="modal__body">
            <h2 className="modal__title">
              {t('events.clientTitle', { name: clientFor.name })}
            </h2>
            <p className="section-hint">{clientFor.phoneMask}</p>
            <EventTimeline
              source={`/admin/clients/${clientFor.id}/events`}
              emptyText={t('events.empty')}
            />
            <div className="modal__actions">
              <button className="btn" type="button" onClick={() => setClientFor(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
}

const APPROVED_STATUSES: ProviderResultStatus[] = [
  ProviderResultStatus.APPROVED,
  ProviderResultStatus.CHOSEN,
  ProviderResultStatus.CONTRACT_ACTIVE,
];

/**
 * Holat rangi.
 *
 * Rad etish va texnik nosozlik bir xil emas: birinchisi provayderning
 * qarori, ikkinchisi shunchaki javob kelmagani. Shuning uchun faqat
 * rad etish qizil bo'ladi.
 */
function statusTone(status: ProviderResultStatus): string {
  if (status === ProviderResultStatus.REJECTED) return 'history-table__status--fail';
  if (isPendingStatus(status)) return 'history-table__status--wait';
  return '';
}

function HistoryTableRow({
  row,
  lang,
  opened,
  onToggle,
  onOpenClient,
}: {
  row: HistoryRow;
  lang: 'uz' | 'ru';
  opened: boolean;
  onToggle: () => void;
  onOpenClient: () => void;
}) {
  const { t } = useI18n();

  return (
    <>
    <tr className="history-table__row" onClick={onToggle}>
      <td className="history-table__date">
        {formatDateTime(row.createdAt)}
        <span className="history-table__time">{formatTime(row.createdAt)}</span>
      </td>
      <td>
        {/* Qatorga bosilsa — shu ariza; ismga bosilsa — mijozning
            hamma urinishlari. Ikkinchisi kerak bo'ladi, chunki odam
            bir necha marta kelgan bo'lishi mumkin. */}
        <button
          className="users-table__name"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenClient();
          }}
        >
          {row.client.name}
        </button>
        <span className="history-table__phone">{row.client.phoneMask}</span>
      </td>
      <td>{row.store?.name ?? '—'}</td>
      <td>{row.salesUser?.name ?? '—'}</td>
      {row.results.map((result) => (
        <td key={result.provider}>
          {APPROVED_STATUSES.includes(result.status) ? (
            <span className="history-table__amount">
              {result.maxAmount === null ? '—' : formatSoum(result.maxAmount, { lang })}
            </span>
          ) : (
            <span className={`history-table__status ${statusTone(result.status)}`}>
              {t(`status.${result.status}`)}
            </span>
          )}
        </td>
      ))}
      <td>
        {row.chosenProvider ? (
          <span className="badge badge--ok">
            {row.chosenProvider === Provider.ALIF ? 'Alif' : 'Uzum'}
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>

    {/* Qatorga bosilganda — o'sha ariza bo'yicha nima bo'lgani. */}
    {opened && (
      <tr className="history-table__details">
        <td colSpan={7}>
          <h3 className="section-title">{t('events.title')}</h3>
          <EventTimeline
            source={`/admin/history/${row.sessionId}/events`}
            emptyText={t('events.empty')}
          />
        </td>
      </tr>
    )}
    </>
  );
}
